import React from "react";
import { FileText, CheckCircle, Edit3, Archive, Eye, HelpCircle } from "lucide-react";
import { useContentManagementContext } from "../ContentManagementContext";

const ContentStatsCards = ({ activeTab }) => {
  const { contentMetrics, metricsLoading, getMetricIcon, getMetricColor } = useContentManagementContext();

  // Icon mapping
  const iconMap = {
    FileText,
    CheckCircle,
    Edit3,
    Archive,
    Eye,
    HelpCircle,
    'question-mark-circle': HelpCircle,
    'documents': FileText,
    'pencil': Edit3
  };

  const getStatsData = () => {
    if (activeTab === "blog-posts" && contentMetrics?.blogs) {
      const blogMetrics = contentMetrics.blogs;
      return [
        {
          title: "Total articles",
          value: blogMetrics.totalArticles?.value || "0",
          icon: iconMap[getMetricIcon(blogMetrics.totalArticles?.icon)] || FileText,
          iconBg: getMetricColor(blogMetrics.totalArticles?.color).split(' ')[0],
          iconColor: getMetricColor(blogMetrics.totalArticles?.color).split(' ')[1]
        },
        {
          title: "Published",
          value: blogMetrics.published?.value || "0",
          icon: iconMap[getMetricIcon(blogMetrics.published?.icon)] || CheckCircle,
          iconBg: getMetricColor(blogMetrics.published?.color).split(' ')[0],
          iconColor: getMetricColor(blogMetrics.published?.color).split(' ')[1]
        },
        {
          title: "Drafts",
          value: blogMetrics.drafts?.value || "0",
          icon: iconMap[getMetricIcon(blogMetrics.drafts?.icon)] || Edit3,
          iconBg: getMetricColor(blogMetrics.drafts?.color).split(' ')[0],
          iconColor: getMetricColor(blogMetrics.drafts?.color).split(' ')[1]
        },
        {
          title: "Archived",
          value: blogMetrics.archived?.value || "0",
          icon: iconMap[getMetricIcon(blogMetrics.archived?.icon)] || Archive,
          iconBg: getMetricColor(blogMetrics.archived?.color).split(' ')[0],
          iconColor: getMetricColor(blogMetrics.archived?.color).split(' ')[1]
        }
      ];
    }

    if (activeTab === "faqs" && contentMetrics?.faqs) {
      const faqMetrics = contentMetrics.faqs;
      return [
        {
          title: "Total FAQs",
          value: faqMetrics.totalFAQs?.value || "0",
          icon: iconMap[getMetricIcon(faqMetrics.totalFAQs?.icon)] || HelpCircle,
          iconBg: getMetricColor(faqMetrics.totalFAQs?.color).split(' ')[0],
          iconColor: getMetricColor(faqMetrics.totalFAQs?.color).split(' ')[1]
        },
        {
          title: "Published",
          value: faqMetrics.published?.value || "0",
          icon: iconMap[getMetricIcon(faqMetrics.published?.icon)] || CheckCircle,
          iconBg: getMetricColor(faqMetrics.published?.color).split(' ')[0],
          iconColor: getMetricColor(faqMetrics.published?.color).split(' ')[1]
        },
        {
          title: "Drafts",
          value: faqMetrics.drafts?.value || "0",
          icon: iconMap[getMetricIcon(faqMetrics.drafts?.icon)] || Edit3,
          iconBg: getMetricColor(faqMetrics.drafts?.color).split(' ')[0],
          iconColor: getMetricColor(faqMetrics.drafts?.color).split(' ')[1]
        },
        {
          title: "Archived",
          value: faqMetrics.archived?.value || "0",
          icon: iconMap[getMetricIcon(faqMetrics.archived?.icon)] || Archive,
          iconBg: getMetricColor(faqMetrics.archived?.color).split(' ')[0],
          iconColor: getMetricColor(faqMetrics.archived?.color).split(' ')[1]
        }
      ];
    }

    // Fallback data for other tabs or when blog metrics are not available
    switch (activeTab) {
      case "announcements":
        return [
          {
            title: "Total announcements",
            value: "45",
            icon: FileText,
            iconBg: "bg-gray-100",
            iconColor: "text-gray-600"
          },
          {
            title: "Published",
            value: "38",
            icon: CheckCircle,
            iconBg: "bg-green-100",
            iconColor: "text-green-600"
          },
          {
            title: "Drafts",
            value: "7",
            icon: Edit3,
            iconBg: "bg-blue-100",
            iconColor: "text-blue-600"
          },
          {
            title: "Archived",
            value: "3",
            icon: Archive,
            iconBg: "bg-purple-100",
            iconColor: "text-purple-600"
          }
        ];
      case "roadmaps":
        return [
          {
            title: "Total roadmaps",
            value: "18",
            icon: FileText,
            iconBg: "bg-gray-100",
            iconColor: "text-gray-600"
          },
          {
            title: "Published",
            value: "15",
            icon: CheckCircle,
            iconBg: "bg-green-100",
            iconColor: "text-green-600"
          },
          {
            title: "Drafts",
            value: "3",
            icon: Edit3,
            iconBg: "bg-blue-100",
            iconColor: "text-blue-600"
          },
          {
            title: "Archived",
            value: "2",
            icon: Archive,
            iconBg: "bg-purple-100",
            iconColor: "text-purple-600"
          }
        ];
      case "faqs":
        return [
          {
            title: "Total FAQs",
            value: "67",
            icon: FileText,
            iconBg: "bg-gray-100",
            iconColor: "text-gray-600"
          },
          {
            title: "Published",
            value: "62",
            icon: CheckCircle,
            iconBg: "bg-green-100",
            iconColor: "text-green-600"
          },
          {
            title: "Drafts",
            value: "5",
            icon: Edit3,
            iconBg: "bg-blue-100",
            iconColor: "text-blue-600"
          },
          {
            title: "Archived",
            value: "8",
            icon: Archive,
            iconBg: "bg-purple-100",
            iconColor: "text-purple-600"
          }
        ];
      default:
        return [];
    }
  };

  const stats = getStatsData();

  // Show loading state when metrics are loading
  if ((activeTab === "blog-posts" || activeTab === "faqs") && metricsLoading) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {[1, 2, 3, 4].map((index) => (
          <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <div className="h-4 bg-gray-200 rounded animate-pulse mb-2"></div>
                <div className="h-6 bg-gray-200 rounded animate-pulse"></div>
              </div>
              <div className="p-2 sm:p-3 rounded-full bg-gray-200 animate-pulse">
                <div className="w-4 h-4 sm:w-5 sm:h-5"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
      {stats.map((stat, index) => (
        <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-600 mb-1">{stat.title}</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900">{stat.value}</p>
            </div>
            <div className={`p-2 sm:p-3 rounded-full flex-shrink-0 ${stat.iconBg}`}>
              <stat.icon className={`w-4 h-4 sm:w-5 sm:h-5 ${stat.iconColor}`} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ContentStatsCards;
